import { useMemo } from 'react'
import { useDomesticV1CartQuery } from '~/app/react-query/domestic/cart/v1'

/**
 * react-query(서버 상태)  / zustand(클라이언트 상태)
 *
 * zustand 에 장바구니 전체 상품에 관련한 데이터를 넣으면 안되는 이유
 * - 두개의 상태가 동기화 되지 않을 수 있음
 * - query를 Refetch 하는 등 데이터 변경될때 zustand 상태가 업데이트 되지 않을 수 있음 -> 관리를 위해 추가 로직 필요하게 됨
 *
 * 따라서, "전체 상품들"은 react query 로 관리한다.
 * 전체 상품들에 파생된 데이터들은 hook을 통해 연산 로직을 처리해서 중복 로직 제거
 *
 *
 * hook 말고 다른 방법
 * - useQuery 의 select 옵션 사용
 * - selector 함수를 사용해서 데이터 가져올 수 있음
 *
 *
 * "전체 상품" 과 "선택한 상품" 을 분리하여 관리한다.
 * - 서버 상태과 클라이언트 상태를 명확히 분리
 */

const useDomesticCartOptions = () => {
  const { data } = useDomesticV1CartQuery()

  const cartItems = data?.productSection.cartItems

  const { allOptionIds, allAvailableOptionIds, unavailableOptionIds } = useMemo(() => {
    const items = cartItems || []

    const allOptionIds: string[] = []
    const allAvailableOptionIds: string[] = []
    const unavailableOptionIds: string[] = []

    items.forEach(item => {
      // DOMESTIC_TRAIN, DOMESTIC_BUS는
      // 짝수 인덱스(0, 2, 4, 6, 8...) 첫 번째 옵션만 처리 (왕복 옵션 처리)
      // 그 외는 모든 옵션 처리
      const options =
        item.type === 'DOMESTIC_TRAIN' || item.type === 'DOMESTIC_BUS'
          ? item.isRoundTrip
            ? item.options.filter(option => option.isSelectable)
            : item.options
          : item.options
      options.forEach(option => {
        const optionId = option.optionId
        allOptionIds.push(optionId)

        if (option.isSaleAvailable) {
          allAvailableOptionIds.push(optionId)
        } else {
          unavailableOptionIds.push(optionId)
        }
      })
    })

    return {
      allOptionIds,
      allAvailableOptionIds,
      unavailableOptionIds,
    }
  }, [cartItems])

  return {
    // 전체 카운트
    totalOptionCount: allOptionIds?.length ?? 0,
    // 전체 선택 가능한 option ids
    allAvailableOptionIds,
    // 장바구니에 담긴 option 이 있는지
    hasOptions: !!allOptionIds?.length,
    // 구매불가 상품 ids
    unavailableOptionIds,
    // 구매불가 상품이 포함되어 있는지
    hasUnavailableOptions: !!unavailableOptionIds.length,
  }
}

export default useDomesticCartOptions
